"use client";

import { Icon } from "@/components/ui/icon";
import { Separator } from "@/components/ui/separator";
import { buildListingUrl } from "@/lib/url";
import { cn } from "@/lib/utils";
import type { ClusterPoint } from "@repo/maps";
import type { ListingInfoAttribute } from "@repo/utils";
import { useFormatter, useTranslations } from "next-intl";
import { MapListingCard } from "./map-listing-card";
import type { MapListingEntry } from "@/lib/types";

export function ListingMapMarker({
  point,
  isHighlighted,
}: {
  point: ClusterPoint<MapListingEntry>;
  isHighlighted: boolean;
}) {
  const t = useTranslations();
  const format = useFormatter();

  if (point.isCluster) {
    return (
      <div className="flex h-[34px] w-[34px] cursor-pointer items-center justify-center rounded-full border-2 border-white bg-emerald-600 text-white shadow-md transition-transform hover:scale-110">
        <span className="font-data text-[12px] font-bold">{point.count}</span>
      </div>
    );
  }

  return (
    <div className="relative">
      {point.item.businessAddressVisibility === "APPROXIMATE" && (
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-600/15 ring-1 ring-emerald-600/35"
          aria-hidden
        />
      )}
      <div
        className={cn(
          "group relative h-[30px] w-fit rounded-[6px] border text-lg font-semibold shadow-md transition-colors",
          {
            "bg-card": !isHighlighted,
            "bg-primary text-primary-foreground": isHighlighted,
          },
        )}
      >
        <div className="flex items-center">
          {point.item.businessType && (
            <div className="shrink-0 px-0.5">
              <Icon
                icon={
                  point.item.businessType.iconName
                    ? (`BusinessType.${point.item.businessType.iconName}` as any)
                    : "Building2Solid"
                }
                className="h-[18px]"
                cdnBusinessTypeIconVariant={isHighlighted ? "invert" : "default"}
              />
            </div>
          )}

          <Separator
            orientation="vertical"
            className={cn({
              "bg-border": !isHighlighted,
              "bg-muted-foreground": isHighlighted,
            })}
          />

          <div className="px-1 font-data text-[13px]">
            {!point.item.askingPrice
              ? t("common.negotiable")
              : format.number(point.item.askingPrice, {
                  style: "currency",
                  currency: point.item.currency,
                  trailingZeroDisplay: "stripIfInteger",
                })}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ListingMapPopup({
  point,
  isActive,
  boostedAttributes,
}: {
  point: ClusterPoint<MapListingEntry>;
  isActive: boolean;
  boostedAttributes?: ListingInfoAttribute[];
}) {
  if (point.isCluster || !point.item.slug) return null;

  return (
    <a
      href={buildListingUrl(point.item)}
      className="block focus-visible:outline-none"
      target="_blank"
      rel="noreferrer"
    >
      <div className="w-[300px]">
        <MapListingCard
          slug={point.item.slug}
          enabled={isActive}
          boostedAttributes={boostedAttributes}
        />
      </div>
    </a>
  );
}
